import { graphWrite } from "../../email/services/graph";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import { mergeAgenda } from "../domain/agenda";
import { dayKeyOf } from "../domain/time";
import type { CalAttendee } from "../types";
import { fetchEventBody } from "./eventDetail";

/*
 * Calendar changes. Each one is sent only when the Undo window ends, and each
 * notifies people the way Outlook would: a move emails the attendees, a
 * proposal or decline goes to the organiser. Times are sent in UTC (a new series in local time).
 */

const utc = (d: Date) => ({ dateTime: d.toISOString().slice(0, 19), timeZone: "UTC" });
const eventPath = (id: string) => `/me/events/${encodeURIComponent(id)}`;

/** Your own meeting: change its time. Attendees get an update. */
export async function rescheduleEvent(
  token: string,
  refresh: RefreshTokenFn | undefined,
  eventId: string,
  start: Date,
  end: Date,
): Promise<void> {
  await graphWrite("PATCH", eventPath(eventId), { start: utc(start), end: utc(end) }, token, refresh);
}

/** Someone else's meeting: propose a new time. Your reply becomes Tentative. */
export async function proposeNewTime(
  token: string,
  refresh: RefreshTokenFn | undefined,
  eventId: string,
  start: Date,
  end: Date,
): Promise<void> {
  await graphWrite(
    "POST",
    `${eventPath(eventId)}/tentativelyAccept`,
    { sendResponse: true, proposedNewTime: { start: utc(start), end: utc(end) } },
    token,
    refresh,
  );
}

/**
 * Reply to an invitation. The organiser is told when `sendResponse` is true;
 * a declined meeting leaves your calendar.
 */
export async function respondToEvent(
  token: string,
  refresh: RefreshTokenFn | undefined,
  eventId: string,
  action: "accept" | "tentativelyAccept" | "decline",
  sendResponse: boolean,
): Promise<void> {
  await graphWrite("POST", `${eventPath(eventId)}/${action}`, { sendResponse }, token, refresh);
}

/** Your own meeting: cancel it. Attendees get a cancellation. */
export async function cancelMeeting(token: string, refresh: RefreshTokenFn | undefined, eventId: string): Promise<void> {
  await graphWrite("POST", `${eventPath(eventId)}/cancel`, { comment: "" }, token, refresh);
}

/** An appointment with nobody invited: take it off your calendar (it goes to Deleted Items). */
export async function removeEvent(token: string, refresh: RefreshTokenFn | undefined, eventId: string): Promise<void> {
  await graphWrite("DELETE", eventPath(eventId), undefined, token, refresh);
}

/**
 * Your own meeting: replace who is invited. Graph sends the invite to anyone
 * added and a cancellation to anyone removed.
 */
export async function setAttendees(
  token: string,
  refresh: RefreshTokenFn | undefined,
  eventId: string,
  attendees: readonly CalAttendee[],
): Promise<void> {
  await graphWrite(
    "PATCH",
    eventPath(eventId),
    { attendees: attendees.map((a) => ({ emailAddress: { address: a.email, name: a.name }, type: a.type })) },
    token,
    refresh,
  );
}

/**
 * Your own meeting: write `agenda` as the invite's agenda block. The body is
 * read again at send time, so nothing added since the screen opened is lost.
 */
export async function saveAgenda(
  token: string,
  refresh: RefreshTokenFn | undefined,
  eventId: string,
  agenda: string,
): Promise<void> {
  const body = await fetchEventBody(token, refresh, eventId);
  await graphWrite(
    "PATCH",
    eventPath(eventId),
    { body: { contentType: "HTML", content: mergeAgenda(body, agenda) } },
    token,
    refresh,
  );
}

/** How a new meeting repeats: every week or every other week on its weekday, with no end date. */
export type Repeat = "none" | "weekly" | "fortnightly";

/** A repeating meeting, sent from the pane: Outlook's own form can't be given a repeat. */
export interface NewSeriesRequest {
  readonly subject: string;
  readonly attendees: readonly { readonly name: string; readonly email: string }[];
  readonly start: Date;
  readonly end: Date;
  /** For an in-person meeting; a Teams meeting gets its join details from Teams. */
  readonly location: string;
  readonly teams: boolean;
  readonly repeat: Exclude<Repeat, "none">;
}

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;
const pad = (n: number) => String(n).padStart(2, "0");
/** Wall-clock time in the user's zone, so a series' 14:00 stays 14:00 after the clocks change. */
const localTime = (d: Date, timeZone: string) => ({ dateTime: `${dayKeyOf(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`, timeZone });

/** Creates the series in the AiVA account's calendar; Graph emails the invites as soon as this succeeds. */
export async function createSeries(token: string, refresh: RefreshTokenFn | undefined, meeting: NewSeriesRequest): Promise<void> {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const place = meeting.teams
    ? { isOnlineMeeting: true, onlineMeetingProvider: "teamsForBusiness" }
    : { location: { displayName: meeting.location } };
  await graphWrite(
    "POST",
    "/me/events",
    {
      subject: meeting.subject,
      start: localTime(meeting.start, timeZone),
      end: localTime(meeting.end, timeZone),
      recurrence: {
        pattern: {
          type: "weekly",
          interval: meeting.repeat === "fortnightly" ? 2 : 1,
          daysOfWeek: [WEEKDAYS[meeting.start.getDay()]],
          firstDayOfWeek: "monday",
        },
        // No end date, as in Outlook; the series can be ended there at any time.
        range: { type: "noEnd", startDate: dayKeyOf(meeting.start) },
      },
      attendees: meeting.attendees.map((a) => ({ emailAddress: { address: a.email, name: a.name }, type: "required" })),
      ...place,
    },
    token,
    refresh,
  );
}

/** A "Focus time" block. Nobody is invited, so nobody is emailed. */
export async function createFocusBlock(
  token: string,
  refresh: RefreshTokenFn | undefined,
  start: Date,
  end: Date,
): Promise<void> {
  await graphWrite(
    "POST",
    "/me/events",
    { subject: "Focus time", start: utc(start), end: utc(end), showAs: "busy", isReminderOn: false },
    token,
    refresh,
  );
}
